import React, { useEffect, useState } from 'react';
import { Box, Container, Grid, Typography, Button, Stack } from '@mui/material';
import useMediaQuery from '@mui/material/useMediaQuery';
import { useAppContext } from '../AppContext';

interface ProductPageProps {
    productTitle: string;
    productVolume: string;
    productPrice: string;
    productDescription: string;
    productIngredients: string[];
    productImages: string[]; // Array of image paths
    productBenefits?: string[]; // Optional array of benefits
    productPath?: string; // Product path to determine styling
    onBackClick: () => void; // Callback for back button
}

export const ProductPage: React.FC<ProductPageProps> = ({
    productTitle,
    productVolume,
    productPrice,
    productDescription,
    productIngredients,
    productImages,
    productBenefits,
    productPath,
    onBackClick,
}) => {
    const [selectedImage, setSelectedImage] = useState(productImages[0]); // Set the default image
    const isMobile = useMediaQuery('(max-width:600px)'); // Check if the screen width is 600px or less
    const { openModal } = useAppContext();
    const isEnergyBoost = productPath === '/product/energy-boost';
    const isProAge = productPath === '/product/pro-age-expert';
    const isHyaluMinerals = productPath === '/product/hyalu-minerals';

    useEffect(() => {
        setSelectedImage(productImages[0]);
    }, [productTitle, productImages]);

    return (
        <>
            <Container sx={{
                paddingTop: '100px',
                backgroundColor: '#fff',
                paddingBottom: '100px',
                position: 'relative',
                overflow: 'hidden',
            }}>
                {/* Orange decorative elements for energy-boost */}
                {isEnergyBoost && (
                    <>
                        <Box
                            sx={{
                                position: 'absolute',
                                width: '300px',
                                height: '300px',
                                borderRadius: '50%',
                                background: 'radial-gradient(circle, rgba(255, 165, 0, 0.15) 0%, transparent 70%)',
                                top: '-100px',
                                right: '-100px',
                                zIndex: 0,
                            }}
                        />
                        <Box
                            sx={{
                                position: 'absolute',
                                width: '200px',
                                height: '200px',
                                borderRadius: '50%',
                                background: 'radial-gradient(circle, rgba(255, 200, 100, 0.2) 0%, transparent 70%)',
                                bottom: '-50px',
                                left: '-50px',
                                zIndex: 0,
                            }}
                        />
                        <Box
                            sx={{
                                position: 'absolute',
                                width: '150px',
                                height: '150px',
                                borderRadius: '50%',
                                background: 'radial-gradient(circle, rgba(255, 218, 185, 0.25) 0%, transparent 70%)',
                                top: '50%',
                                left: '10%',
                                zIndex: 0,
                            }}
                        />
                    </>
                )}
                {/* Teal/Green decorative elements for hyalu-minerals */}
                {isHyaluMinerals && (
                    <>
                        <Box
                            sx={{
                                position: 'absolute',
                                width: '300px',
                                height: '300px',
                                borderRadius: '50%',
                                background: 'radial-gradient(circle, rgba(64, 224, 208, 0.15) 0%, transparent 70%)',
                                top: '-100px',
                                right: '-100px',
                                zIndex: 0,
                            }}
                        />
                        <Box
                            sx={{
                                position: 'absolute',
                                width: '200px',
                                height: '200px',
                                borderRadius: '50%',
                                background: 'radial-gradient(circle, rgba(72, 209, 204, 0.2) 0%, transparent 70%)',
                                bottom: '-50px',
                                left: '-50px',
                                zIndex: 0,
                            }}
                        />
                        <Box
                            sx={{
                                position: 'absolute',
                                width: '150px',
                                height: '150px',
                                borderRadius: '50%',
                                background: 'radial-gradient(circle, rgba(175, 238, 238, 0.25) 0%, transparent 70%)',
                                top: '50%',
                                left: '10%',
                                zIndex: 0,
                            }}
                        />
                    </>
                )}
                {/* Purple decorative elements for pro-age-expert */}
                {isProAge && (
                    <>
                        <Box
                            sx={{
                                position: 'absolute',
                                width: '300px',
                                height: '300px',
                                borderRadius: '50%',
                                background: 'radial-gradient(circle, rgba(186, 85, 211, 0.15) 0%, transparent 70%)',
                                top: '-100px',
                                right: '-100px',
                                zIndex: 0,
                            }}
                        />
                        <Box
                            sx={{
                                position: 'absolute',
                                width: '200px',
                                height: '200px',
                                borderRadius: '50%',
                                background: 'radial-gradient(circle, rgba(221, 160, 221, 0.2) 0%, transparent 70%)',
                                bottom: '-50px',
                                left: '-50px',
                                zIndex: 0,
                            }}
                        />
                        <Box
                            sx={{
                                position: 'absolute',
                                width: '150px',
                                height: '150px',
                                borderRadius: '50%',
                                background: 'radial-gradient(circle, rgba(230, 230, 250, 0.25) 0%, transparent 70%)',
                                top: '50%',
                                left: '10%',
                                zIndex: 0,
                            }}
                        />
                    </>
                )}
                <Box sx={{ marginBottom: '20px' }}>
                    <Button
                        variant="text"
                        sx={{ textTransform: 'none', fontWeight: '500', fontSize: '16px', color: 'rgba(61, 61, 61, 1)', fontFamily: 'Quicksand' }}
                        onClick={onBackClick}
                    >
                        &#8592; BACK TO PRODUCTS
                    </Button>
                </Box>
                <Grid container spacing={4} sx={{ position: 'relative', zIndex: 1 }}>
                    {/* Thumbnails - show on the left side on larger screens */}
                    {!isMobile && (
                        <Grid item xs={12} md={2}>
                            <Grid container spacing={0.5} sx={{ height: '100%' }}>
                                {productImages.map((image, index) => (
                                    <Grid item xs={12} key={index}>
                                        <Box
                                            component="img"
                                            src={image}
                                            alt={`Thumbnail ${index + 1}`}
                                            sx={{
                                                width: '100%',
                                                maxHeight: '130px',
                                                height: '130px',
                                                objectFit: 'contain',
                                                borderRadius: '22px',
                                                cursor: 'pointer',
                                                border: selectedImage === image ? '2px solid #003087' : 'none',
                                                marginBottom: '4px',
                                            }}
                                            onClick={() => setSelectedImage(image)}
                                        />
                                    </Grid>
                                ))}
                            </Grid>
                        </Grid>
                    )}

                    {/* Selected Image */}
                    <Grid item xs={12} md={isMobile ? 12 : 5}>
                        <Box
                            component="img"
                            src={selectedImage}
                            alt="Product"
                            sx={{
                                width: '100%',
                                height: isMobile ? '400px' : '600px',
                                objectFit: 'contain',
                                borderRadius: '12px',
                                backgroundColor: isEnergyBoost
                                    ? 'rgba(255, 245, 230, 0.8)'
                                    : isProAge
                                        ? 'rgba(245, 240, 255, 0.8)'
                                        : isHyaluMinerals
                                            ? 'rgba(230, 250, 245, 0.8)'
                                            : 'rgba(240, 248, 255, 1)',
                                position: 'relative',
                                zIndex: 1,
                            }}
                        />
                    </Grid>
                    {isMobile && (
                        <Grid item xs={12} >
                            <Grid container spacing={0.5}>
                                {productImages.map((image, index) => (
                                    <Grid item xs={3} key={index}>
                                        <Box
                                            component="img"
                                            src={image}
                                            alt={`Thumbnail ${index + 1}`}
                                            sx={{
                                                width: '100%',
                                                maxHeight: '70px',
                                                height: '70px',
                                                objectFit: 'contain',
                                                borderRadius: '12px',
                                                cursor: 'pointer',
                                                border: selectedImage === image ? '2px solid #003087' : 'none',
                                            }}
                                            onClick={() => setSelectedImage(image)}
                                        />
                                    </Grid>
                                ))}
                            </Grid>
                        </Grid>
                    )}
                    {/* Product Details */}
                    <Grid item xs={12} md={5}>
                        <Stack
                            spacing={2}
                            sx={{
                                height: '100%',
                                justifyContent: 'space-between',
                            }}
                        >
                            {/* Title and Volume */}
                            <Stack>
                                <Typography
                                    variant='h4'
                                    sx={{
                                        fontFamily: 'ClaireMurphy',
                                        fontSize: '36px',
                                        fontWeight: 500,
                                        color: 'rgba(61, 61, 61, 1)',
                                        marginBottom: '10px',
                                    }}
                                >
                                    {productTitle}
                                </Typography>
                                <Typography
                                    sx={{
                                        fontFamily: 'Quicksand',
                                        fontSize: '18px',
                                        fontWeight: 400,
                                        color: 'rgba(136, 136, 136, 1)',
                                    }}
                                >
                                    {productVolume}
                                </Typography>
                            </Stack>

                            {/* Description and Ingredients */}
                            <Stack spacing={2}>
                                <Typography
                                    sx={{
                                        fontFamily: 'Quicksand',
                                        fontSize: '16px',
                                        fontWeight: 400,
                                        color: 'rgba(61, 61, 61, 1)',
                                        lineHeight: '26.1px'
                                    }}
                                >
                                    <b>Description</b><br />
                                    {productDescription}
                                </Typography>
                                <Typography
                                    sx={{
                                        fontFamily: 'Quicksand',
                                        fontSize: '16px',
                                        fontWeight: 400,
                                        color: 'rgba(61, 61, 61, 1)',
                                        lineHeight: '26.1px'
                                    }}
                                >
                                    <b>Main Ingredients</b><br />
                                    {productIngredients.map((ingredient, index) => (
                                        <span key={index}>&bull; {ingredient}<br /></span>
                                    ))}
                                </Typography>
                            </Stack>

                            {/* Price and Buy Now Button */}
                            <Stack direction="row" alignItems="center" spacing={2} sx={{ width: '100%' }}>
                                <Box sx={{ display: 'flex', flexDirection: 'column' }}>
                                    {productPrice ? (
                                        <>
                                            <Typography
                                                sx={{
                                                    fontFamily: 'Quicksand',
                                                    fontSize: '16px',
                                                    fontWeight: 400,
                                                    color: 'rgba(61, 61, 61, 1)',
                                                    lineHeight: '1.2',
                                                }}
                                            >
                                                RM
                                            </Typography>
                                            <Typography
                                                sx={{
                                                    fontFamily: 'Quicksand',
                                                    fontSize: '24px',
                                                    fontWeight: 600,
                                                    color: 'rgba(61, 61, 61, 1)',
                                                    lineHeight: '1.2',
                                                }}
                                            >
                                                {productPrice.replace('RM ', '')}
                                            </Typography>
                                        </>
                                    ) : (
                                        <Typography
                                            sx={{
                                                fontFamily: 'Quicksand',
                                                fontSize: '24px',
                                                fontWeight: 600,
                                                color: 'rgba(61, 61, 61, 1)',
                                                lineHeight: '1.2',
                                            }}
                                        >
                                            {productVolume}
                                        </Typography>
                                    )}
                                </Box>
                                <Button
                                    variant="outlined"
                                    sx={{
                                        position: 'relative',
                                        overflow: 'hidden',
                                        borderColor: 'rgba(0, 48, 135, 1)',
                                        color: 'rgba(0, 48, 135, 1)',
                                        borderRadius: '50px',
                                        textTransform: 'uppercase',
                                        fontWeight: 600,
                                        flex: 1,
                                        '&::before': {
                                            content: '""',
                                            position: 'absolute',
                                            top: 0,
                                            left: 0,
                                            width: '100%',
                                            height: '100%',
                                            backgroundColor: 'rgba(0, 48, 135, 1)',
                                            zIndex: 0,
                                            transform: 'translateX(-100%)',
                                            transition: 'transform 0.4s ease',
                                        },
                                        '&:hover::before': {
                                            transform: 'translateX(0)',
                                        },
                                        '&:hover': {
                                            borderColor: '#003365',
                                            color: '#fff',
                                        },
                                        '& .button-text': {
                                            position: 'relative',
                                            zIndex: 1,
                                        },
                                    }}
                                    onClick={(event) => {
                                        event.stopPropagation(); // Stop event propagation
                                        openModal();
                                    }}
                                >
                                    <span className="button-text">Buy Now</span>
                                </Button>

                            </Stack>
                        </Stack>
                    </Grid>

                    {/* Thumbnails - show at the bottom on mobile */}

                </Grid>
            </Container>

            {/* Benefits Section - Only show if benefits are provided - Full Width */}
            {productBenefits && productBenefits.length > 0 && (
                <Box
                    sx={{
                        width: '100vw',
                        marginTop: { xs: '60px', sm: '80px', md: '120px' },
                        padding: { xs: '40px 16px', sm: '60px 24px', md: '100px 40px' },
                        background: isEnergyBoost
                            ? 'linear-gradient(135deg, rgba(255, 245, 230, 0.4) 0%, rgba(255, 235, 205, 0.5) 25%, rgba(255, 218, 185, 0.4) 50%, rgba(255, 200, 100, 0.5) 75%, rgba(255, 165, 0, 0.3) 100%)'
                            : isProAge
                                ? 'linear-gradient(135deg, rgba(245, 240, 255, 0.4) 0%, rgba(230, 230, 250, 0.5) 25%, rgba(221, 160, 221, 0.4) 50%, rgba(186, 85, 211, 0.5) 75%, rgba(138, 43, 226, 0.3) 100%)'
                                : isHyaluMinerals
                                    ? 'linear-gradient(135deg, rgba(230, 250, 245, 0.4) 0%, rgba(175, 238, 238, 0.5) 25%, rgba(72, 209, 204, 0.4) 50%, rgba(64, 224, 208, 0.5) 75%, rgba(32, 178, 170, 0.3) 100%)'
                                    : 'linear-gradient(135deg, rgba(173, 216, 230, 0.3) 0%, rgba(135, 206, 250, 0.4) 25%, rgba(0, 191, 255, 0.3) 50%, rgba(70, 130, 180, 0.4) 75%, rgba(0, 48, 135, 0.2) 100%)',
                        position: 'relative',
                        overflow: 'hidden',
                        marginLeft: 'calc(-50vw + 50%)',
                    }}
                >
                    {/* Water ripple layers */}
                    <Box
                        sx={{
                            position: 'absolute',
                            width: '100%',
                            height: '100%',
                            top: 0,
                            left: 0,
                            overflow: 'hidden',
                            zIndex: 0,
                        }}
                    >
                        {/* Ripple 1 */}
                        <Box
                            sx={{
                                position: 'absolute',
                                width: '200%',
                                height: '200%',
                                top: '-50%',
                                left: '-50%',
                                background: isEnergyBoost
                                    ? 'radial-gradient(circle, rgba(255, 200, 100, 0.2) 0%, transparent 70%)'
                                    : isProAge
                                        ? 'radial-gradient(circle, rgba(221, 160, 221, 0.2) 0%, transparent 70%)'
                                        : isHyaluMinerals
                                            ? 'radial-gradient(circle, rgba(72, 209, 204, 0.2) 0%, transparent 70%)'
                                            : 'radial-gradient(circle, rgba(135, 206, 250, 0.15) 0%, transparent 70%)',
                                borderRadius: '50%',
                                animation: 'ripple 8s ease-in-out infinite',
                                '@keyframes ripple': {
                                    '0%': { transform: 'scale(0.8)', opacity: 0.6 },
                                    '50%': { transform: 'scale(1.2)', opacity: 0.3 },
                                    '100%': { transform: 'scale(0.8)', opacity: 0.6 },
                                },
                            }}
                        />
                        {/* Ripple 2 */}
                        <Box
                            sx={{
                                position: 'absolute',
                                width: '200%',
                                height: '200%',
                                top: '-50%',
                                left: '-50%',
                                background: isEnergyBoost
                                    ? 'radial-gradient(circle, rgba(255, 218, 185, 0.25) 0%, transparent 70%)'
                                    : isProAge
                                        ? 'radial-gradient(circle, rgba(230, 230, 250, 0.25) 0%, transparent 70%)'
                                        : isHyaluMinerals
                                            ? 'radial-gradient(circle, rgba(175, 238, 238, 0.25) 0%, transparent 70%)'
                                            : 'radial-gradient(circle, rgba(173, 216, 230, 0.2) 0%, transparent 70%)',
                                borderRadius: '50%',
                                animation: 'ripple 10s ease-in-out infinite',
                                animationDelay: '2s',
                            }}
                        />
                        {/* Ripple 3 */}
                        <Box
                            sx={{
                                position: 'absolute',
                                width: '200%',
                                height: '200%',
                                top: '-50%',
                                left: '-50%',
                                background: isEnergyBoost
                                    ? 'radial-gradient(circle, rgba(255, 165, 0, 0.2) 0%, transparent 70%)'
                                    : isProAge
                                        ? 'radial-gradient(circle, rgba(186, 85, 211, 0.2) 0%, transparent 70%)'
                                        : isHyaluMinerals
                                            ? 'radial-gradient(circle, rgba(64, 224, 208, 0.2) 0%, transparent 70%)'
                                            : 'radial-gradient(circle, rgba(0, 191, 255, 0.15) 0%, transparent 70%)',
                                borderRadius: '50%',
                                animation: 'ripple 12s ease-in-out infinite',
                                animationDelay: '4s',
                            }}
                        />
                        {/* Wave effect */}
                        <Box
                            sx={{
                                position: 'absolute',
                                width: '100%',
                                height: '100%',
                                background: isEnergyBoost
                                    ? 'repeating-linear-gradient(0deg, transparent, transparent 2px, rgba(255, 200, 100, 0.15) 2px, rgba(255, 200, 100, 0.15) 4px)'
                                    : isProAge
                                        ? 'repeating-linear-gradient(0deg, transparent, transparent 2px, rgba(221, 160, 221, 0.15) 2px, rgba(221, 160, 221, 0.15) 4px)'
                                        : isHyaluMinerals
                                            ? 'repeating-linear-gradient(0deg, transparent, transparent 2px, rgba(72, 209, 204, 0.15) 2px, rgba(72, 209, 204, 0.15) 4px)'
                                            : 'repeating-linear-gradient(0deg, transparent, transparent 2px, rgba(135, 206, 250, 0.1) 2px, rgba(135, 206, 250, 0.1) 4px)',
                                animation: 'wave 15s linear infinite',
                                '@keyframes wave': {
                                    '0%': { backgroundPosition: '0 0' },
                                    '100%': { backgroundPosition: '0 100px' },
                                },
                            }}
                        />
                    </Box>

                    <Container maxWidth="lg">
                        {/* Mobile/Tablet: Stacked Layout */}
                        {isMobile && (
                            <Box
                                sx={{
                                    display: 'flex',
                                    flexDirection: 'column',
                                    alignItems: 'center',
                                    gap: { xs: '32px', sm: '40px' },
                                    padding: { xs: '20px 0', sm: '40px 0' },
                                    zIndex: 1,
                                }}
                            >
                                {/* Product Image */}
                                <Box
                                    sx={{
                                        width: { xs: '200px', sm: '280px' },
                                        height: { xs: '200px', sm: '280px' },
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'center',
                                        filter: isEnergyBoost
                                            ? 'drop-shadow(0 15px 30px rgba(255, 165, 0, 0.3))'
                                            : isProAge
                                                ? 'drop-shadow(0 15px 30px rgba(186, 85, 211, 0.3))'
                                                : isHyaluMinerals
                                                    ? 'drop-shadow(0 15px 30px rgba(64, 224, 208, 0.3))'
                                                    : 'drop-shadow(0 15px 30px rgba(135, 206, 250, 0.3))',
                                    }}
                                >
                                    <Box
                                        component="img"
                                        src={productImages[0]}
                                        alt={productTitle}
                                        sx={{
                                            width: '100%',
                                            height: '100%',
                                            objectFit: 'contain',
                                        }}
                                    />
                                </Box>

                                {/* Benefits Grid */}
                                <Grid container spacing={2} sx={{ maxWidth: { xs: '100%', sm: '600px' } }}>
                                    {productBenefits.map((benefit, index) => (
                                        <Grid item xs={12} sm={6} key={index}>
                                            <Box
                                                sx={{
                                                    background: 'rgba(255, 255, 255, 0.9)',
                                                    backdropFilter: 'blur(15px)',
                                                    borderRadius: '20px',
                                                    padding: { xs: '16px', sm: '20px' },
                                                    boxShadow: isEnergyBoost
                                                        ? '0 8px 24px rgba(255, 165, 0, 0.2)'
                                                        : '0 8px 24px rgba(135, 206, 250, 0.2)',
                                                    border: isEnergyBoost
                                                        ? '1px solid rgba(255, 200, 100, 0.3)'
                                                        : '1px solid rgba(135, 206, 250, 0.3)',
                                                    display: 'flex',
                                                    flexDirection: 'row',
                                                    alignItems: 'center',
                                                    gap: { xs: '12px', sm: '16px' },
                                                    animation: `fadeInUp 0.6s ease-out ${index * 0.1}s both`,
                                                }}
                                            >
                                                {/* Checkmark Circle */}
                                                <Box
                                                    sx={{
                                                        width: { xs: '44px', sm: '52px' },
                                                        height: { xs: '44px', sm: '52px' },
                                                        borderRadius: '50%',
                                                        background: isEnergyBoost
                                                            ? 'linear-gradient(135deg, rgba(255, 200, 100, 1) 0%, rgba(255, 165, 0, 1) 50%, rgba(255, 140, 0, 1) 100%)'
                                                            : 'linear-gradient(135deg, rgba(135, 206, 250, 1) 0%, rgba(0, 191, 255, 1) 50%, rgba(0, 48, 135, 1) 100%)',
                                                        display: 'flex',
                                                        alignItems: 'center',
                                                        justifyContent: 'center',
                                                        flexShrink: 0,
                                                        boxShadow: isEnergyBoost
                                                            ? '0 6px 20px rgba(255, 165, 0, 0.4)'
                                                            : isProAge
                                                                ? '0 6px 20px rgba(186, 85, 211, 0.4)'
                                                                : isHyaluMinerals
                                                                    ? '0 6px 20px rgba(64, 224, 208, 0.4)'
                                                                    : '0 6px 20px rgba(135, 206, 250, 0.4)',
                                                    }}
                                                >
                                                    <Typography
                                                        sx={{
                                                            color: '#fff',
                                                            fontSize: { xs: '20px', sm: '24px' },
                                                            fontWeight: 'bold',
                                                        }}
                                                    >
                                                        ✓
                                                    </Typography>
                                                </Box>
                                                {/* Benefit Text */}
                                                <Typography
                                                    sx={{
                                                        fontFamily: 'Quicksand',
                                                        fontSize: { xs: '13px', sm: '14px' },
                                                        fontWeight: 600,
                                                        color: 'rgba(61, 61, 61, 1)',
                                                        lineHeight: { xs: '18px', sm: '20px' },
                                                        flex: 1,
                                                    }}
                                                >
                                                    {benefit}
                                                </Typography>
                                            </Box>
                                        </Grid>
                                    ))}
                                </Grid>
                            </Box>
                        )}

                        {/* Desktop: Circular Layout */}
                        {!isMobile && (
                            <Box
                                sx={{
                                    position: 'relative',
                                    width: '100%',
                                    minHeight: { sm: '700px', md: '900px', lg: '950px' },
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    padding: { sm: '40px 20px', md: '60px', lg: '80px' },
                                    zIndex: 1,
                                }}
                            >
                                {/* Decorative background circles */}
                                <Box
                                    sx={{
                                        position: 'absolute',
                                        width: '100%',
                                        height: '100%',
                                        top: 0,
                                        left: 0,
                                        overflow: 'hidden',
                                        zIndex: 0,
                                    }}
                                >
                                    {/* Large gradient circles */}
                                    <Box
                                        sx={{
                                            position: 'absolute',
                                            width: { sm: '500px', md: '600px', lg: '700px' },
                                            height: { sm: '500px', md: '600px', lg: '700px' },
                                            borderRadius: '50%',
                                            background: isEnergyBoost
                                                ? 'radial-gradient(circle, rgba(255, 200, 100, 0.25) 0%, transparent 70%)'
                                                : 'radial-gradient(circle, rgba(135, 206, 250, 0.2) 0%, transparent 70%)',
                                            top: '50%',
                                            left: '50%',
                                            transform: 'translate(-50%, -50%)',
                                            animation: 'pulse 4s ease-in-out infinite',
                                            '@keyframes pulse': {
                                                '0%, 100%': { opacity: 0.5, transform: 'translate(-50%, -50%) scale(1)' },
                                                '50%': { opacity: 0.8, transform: 'translate(-50%, -50%) scale(1.1)' },
                                            },
                                        }}
                                    />
                                    <Box
                                        sx={{
                                            position: 'absolute',
                                            width: { sm: '380px', md: '450px', lg: '520px' },
                                            height: { sm: '380px', md: '450px', lg: '520px' },
                                            borderRadius: '50%',
                                            background: isEnergyBoost
                                                ? 'radial-gradient(circle, rgba(255, 218, 185, 0.2) 0%, transparent 70%)'
                                                : 'radial-gradient(circle, rgba(173, 216, 230, 0.15) 0%, transparent 70%)',
                                            top: '50%',
                                            left: '50%',
                                            transform: 'translate(-50%, -50%)',
                                            animation: 'pulse 3s ease-in-out infinite',
                                            animationDelay: '1s',
                                        }}
                                    />
                                </Box>

                                {/* Center Product Image with glow effect */}
                                <Box
                                    sx={{
                                        position: 'relative',
                                        zIndex: 2,
                                        width: { sm: '350px', md: '450px', lg: '500px' },
                                        height: { sm: '350px', md: '450px', lg: '500px' },
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'center',
                                        filter: isEnergyBoost
                                            ? 'drop-shadow(0 20px 40px rgba(255, 165, 0, 0.3))'
                                            : isProAge
                                                ? 'drop-shadow(0 20px 40px rgba(186, 85, 211, 0.3))'
                                                : isHyaluMinerals
                                                    ? 'drop-shadow(0 20px 40px rgba(64, 224, 208, 0.3))'
                                                    : 'drop-shadow(0 20px 40px rgba(135, 206, 250, 0.3))',
                                        animation: 'float 6s ease-in-out infinite',
                                        '@keyframes float': {
                                            '0%, 100%': { transform: 'translateY(0px)' },
                                            '50%': { transform: 'translateY(-20px)' },
                                        },
                                    }}
                                >
                                    {/* Water ripple glow rings */}
                                    <Box
                                        sx={{
                                            position: 'absolute',
                                            width: '120%',
                                            height: '120%',
                                            borderRadius: '50%',
                                            background: isEnergyBoost
                                                ? 'radial-gradient(circle, rgba(255, 200, 100, 0.25) 0%, transparent 70%)'
                                                : isProAge
                                                    ? 'radial-gradient(circle, rgba(221, 160, 221, 0.25) 0%, transparent 70%)'
                                                    : isHyaluMinerals
                                                        ? 'radial-gradient(circle, rgba(72, 209, 204, 0.25) 0%, transparent 70%)'
                                                        : 'radial-gradient(circle, rgba(135, 206, 250, 0.2) 0%, transparent 70%)',
                                            animation: 'waterGlow 4s ease-in-out infinite',
                                            '@keyframes waterGlow': {
                                                '0%, 100%': { opacity: 0.4, transform: 'scale(1)' },
                                                '50%': { opacity: 0.7, transform: 'scale(1.1)' },
                                            },
                                        }}
                                    />
                                    <Box
                                        sx={{
                                            position: 'absolute',
                                            width: '140%',
                                            height: '140%',
                                            borderRadius: '50%',
                                            background: isEnergyBoost
                                                ? 'radial-gradient(circle, rgba(255, 218, 185, 0.2) 0%, transparent 70%)'
                                                : isProAge
                                                    ? 'radial-gradient(circle, rgba(230, 230, 250, 0.2) 0%, transparent 70%)'
                                                    : isHyaluMinerals
                                                        ? 'radial-gradient(circle, rgba(175, 238, 238, 0.2) 0%, transparent 70%)'
                                                        : 'radial-gradient(circle, rgba(173, 216, 230, 0.15) 0%, transparent 70%)',
                                            animation: 'waterGlow 5s ease-in-out infinite',
                                            animationDelay: '1s',
                                        }}
                                    />
                                    <Box
                                        sx={{
                                            position: 'absolute',
                                            width: '160%',
                                            height: '160%',
                                            borderRadius: '50%',
                                            background: isEnergyBoost
                                                ? 'radial-gradient(circle, rgba(255, 165, 0, 0.15) 0%, transparent 70%)'
                                                : isProAge
                                                    ? 'radial-gradient(circle, rgba(186, 85, 211, 0.15) 0%, transparent 70%)'
                                                    : isHyaluMinerals
                                                        ? 'radial-gradient(circle, rgba(64, 224, 208, 0.15) 0%, transparent 70%)'
                                                        : 'radial-gradient(circle, rgba(0, 191, 255, 0.1) 0%, transparent 70%)',
                                            animation: 'waterGlow 6s ease-in-out infinite',
                                            animationDelay: '2s',
                                        }}
                                    />
                                    <Box
                                        component="img"
                                        src={productImages[0]}
                                        alt={productTitle}
                                        sx={{
                                            width: '100%',
                                            height: '100%',
                                            objectFit: 'contain',
                                            position: 'relative',
                                            zIndex: 1,
                                        }}
                                    />
                                </Box>

                                {/* Benefits arranged around the image */}
                                {productBenefits.map((benefit, index) => {
                                    const angle = (index * 360) / productBenefits.length;
                                    const radiusMap: Record<string, number> = { sm: 320, md: 380, lg: 420 };
                                    const radius = radiusMap.md || 380;
                                    const x = Math.cos((angle - 90) * (Math.PI / 180)) * radius;
                                    const y = Math.sin((angle - 90) * (Math.PI / 180)) * radius;

                                    return (
                                        <Box
                                            key={index}
                                            sx={{
                                                position: 'absolute',
                                                left: `calc(50% + ${x}px)`,
                                                top: `calc(50% + ${y}px)`,
                                                transform: 'translate(-50%, -50%)',
                                                zIndex: 3,
                                                width: { sm: '180px', md: '220px', lg: '240px' },
                                                display: 'flex',
                                                flexDirection: 'column',
                                                alignItems: 'center',
                                                animation: `fadeInUp 0.6s ease-out ${index * 0.1}s both`,
                                                '@keyframes fadeInUp': {
                                                    '0%': { opacity: 0, transform: 'translate(-50%, -40%)' },
                                                    '100%': { opacity: 1, transform: 'translate(-50%, -50%)' },
                                                },
                                            }}
                                        >
                                            {/* Connecting line */}
                                            <Box
                                                sx={{
                                                    position: 'absolute',
                                                    width: '2px',
                                                    height: { sm: '120px', md: '140px', lg: '160px' },
                                                    background: isEnergyBoost
                                                        ? 'linear-gradient(to top, rgba(255, 165, 0, 0.2), transparent)'
                                                        : isProAge
                                                            ? 'linear-gradient(to top, rgba(186, 85, 211, 0.2), transparent)'
                                                            : isHyaluMinerals
                                                                ? 'linear-gradient(to top, rgba(64, 224, 208, 0.2), transparent)'
                                                                : 'linear-gradient(to top, rgba(0, 48, 135, 0.2), transparent)',
                                                    bottom: '100%',
                                                    transform: `rotate(${angle}deg)`,
                                                    transformOrigin: 'bottom center',
                                                }}
                                            />

                                            {/* Benefit Card */}
                                            <Box
                                                sx={{
                                                    background: 'rgba(255, 255, 255, 0.9)',
                                                    backdropFilter: 'blur(15px)',
                                                    borderRadius: '24px',
                                                    padding: { sm: '20px 16px', md: '24px 20px' },
                                                    boxShadow: isEnergyBoost
                                                        ? '0 8px 24px rgba(255, 165, 0, 0.2)'
                                                        : isProAge
                                                            ? '0 8px 24px rgba(186, 85, 211, 0.2)'
                                                            : isHyaluMinerals
                                                                ? '0 8px 24px rgba(64, 224, 208, 0.2)'
                                                                : '0 8px 24px rgba(135, 206, 250, 0.2)',
                                                    border: isEnergyBoost
                                                        ? '1px solid rgba(255, 200, 100, 0.3)'
                                                        : isProAge
                                                            ? '1px solid rgba(221, 160, 221, 0.3)'
                                                            : isHyaluMinerals
                                                                ? '1px solid rgba(72, 209, 204, 0.3)'
                                                                : '1px solid rgba(135, 206, 250, 0.3)',
                                                    transition: 'all 0.3s ease',
                                                    '&:hover': {
                                                        transform: 'translateY(-8px) scale(1.05)',
                                                        boxShadow: isEnergyBoost
                                                            ? '0 12px 32px rgba(255, 165, 0, 0.35)'
                                                            : isProAge
                                                                ? '0 12px 32px rgba(186, 85, 211, 0.35)'
                                                                : isHyaluMinerals
                                                                    ? '0 12px 32px rgba(64, 224, 208, 0.35)'
                                                                    : '0 12px 32px rgba(135, 206, 250, 0.35)',
                                                        background: 'rgba(255, 255, 255, 0.95)',
                                                    },
                                                }}
                                            >
                                                {/* Checkmark Circle */}
                                                <Box
                                                    sx={{
                                                        width: { sm: '52px', md: '60px', lg: '64px' },
                                                        height: { sm: '52px', md: '60px', lg: '64px' },
                                                        borderRadius: '50%',
                                                        background: isEnergyBoost
                                                            ? 'linear-gradient(135deg, rgba(255, 200, 100, 1) 0%, rgba(255, 165, 0, 1) 50%, rgba(255, 140, 0, 1) 100%)'
                                                            : isProAge
                                                                ? 'linear-gradient(135deg, rgba(221, 160, 221, 1) 0%, rgba(186, 85, 211, 1) 50%, rgba(138, 43, 226, 1) 100%)'
                                                                : isHyaluMinerals
                                                                    ? 'linear-gradient(135deg, rgba(72, 209, 204, 1) 0%, rgba(64, 224, 208, 1) 50%, rgba(32, 178, 170, 1) 100%)'
                                                                    : 'linear-gradient(135deg, rgba(135, 206, 250, 1) 0%, rgba(0, 191, 255, 1) 50%, rgba(0, 48, 135, 1) 100%)',
                                                        display: 'flex',
                                                        alignItems: 'center',
                                                        justifyContent: 'center',
                                                        marginBottom: { sm: '14px', md: '16px' },
                                                        margin: '0 auto',
                                                        boxShadow: isEnergyBoost
                                                            ? '0 6px 20px rgba(255, 165, 0, 0.4)'
                                                            : isProAge
                                                                ? '0 6px 20px rgba(186, 85, 211, 0.4)'
                                                                : isHyaluMinerals
                                                                    ? '0 6px 20px rgba(64, 224, 208, 0.4)'
                                                                    : '0 6px 20px rgba(135, 206, 250, 0.4)',
                                                        transition: 'all 0.3s ease',
                                                        '&:hover': {
                                                            transform: 'scale(1.1) rotate(5deg)',
                                                            boxShadow: isEnergyBoost
                                                                ? '0 8px 24px rgba(255, 165, 0, 0.5)'
                                                                : '0 8px 24px rgba(135, 206, 250, 0.5)',
                                                        },
                                                    }}
                                                >
                                                    <Typography
                                                        sx={{
                                                            color: '#fff',
                                                            fontSize: { sm: '26px', md: '30px', lg: '32px' },
                                                            fontWeight: 'bold',
                                                        }}
                                                    >
                                                        ✓
                                                    </Typography>
                                                </Box>
                                                {/* Benefit Text */}
                                                <Typography
                                                    sx={{
                                                        fontFamily: 'Quicksand',
                                                        fontSize: { sm: '14px', md: '15px', lg: '16px' },
                                                        fontWeight: 600,
                                                        color: 'rgba(61, 61, 61, 1)',
                                                        textAlign: 'center',
                                                        lineHeight: { sm: '20px', md: '22px', lg: '24px' },
                                                    }}
                                                >
                                                    {benefit}
                                                </Typography>
                                            </Box>
                                        </Box>
                                    );
                                })}
                            </Box>
                        )}
                    </Container>
                </Box>
            )}
        </>
    );
};

export default ProductPage;
